import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useGoogleLogin } from '@react-oauth/google';
import { useAppDispatch } from '../../hooks/redux';
import { googleLogin } from '../../store/slices/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import useBackButton from '../../hooks/useBackButton';

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  actionLabel?: string;
}

const LoginPromptModal: React.FC<LoginPromptModalProps> = ({ 
  isOpen, 
  onClose, 
  message = "Sign in to continue",
  actionLabel = "this action"
}) => {
  useBackButton(onClose, isOpen, 150);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isOpen) {
      localStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
    }
  }, [isOpen]);

  const googleLoginAction = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const result = await dispatch(googleLogin({ access_token: tokenResponse.access_token }));
        if (googleLogin.fulfilled.match(result)) {
          const payload = result.payload as any;
          onClose();
          toast.success('Successfully signed in!');
          if (payload.is_new_user) {
            navigate('/profile/complete');
          }
        }
      } catch (error) {
        console.error('Google login failed', error);
        toast.error('Login failed. Please try again.');
      }
    },
    onError: () => {
      toast.error('Google login failed');
    },
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden z-10"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <div className="p-6 pb-12 sm:p-8 text-center">
              {/* Logo/Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-teal-500/30">
                <span className="text-2xl font-bold text-white">C</span>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Join ClickCollab
              </h2>
              <p className="text-gray-500 mb-8">
                {message}
              </p>

              {/* Google Sign In Button */}
              <button
                onClick={() => googleLoginAction()}
                className="w-full max-w-[320px] mx-auto flex items-center justify-center gap-3 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-4 px-6 rounded-xl transition-all duration-200 mb-4"
              >
                <img
                  src="https://www.google.com/favicon.ico"
                  alt="Google"
                  className="w-5 h-5"
                />
                Continue with Google
              </button>

              {/* Email Sign Up Link */}
              <Link
                to="/register"
                onClick={onClose}
                className="flex items-center justify-center w-full max-w-[320px] mx-auto bg-black hover:bg-gray-800 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 mb-6"
              >
                Sign up with Email
              </Link>

              {/* Login Link */}
              <p className="text-sm text-gray-500">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  onClick={onClose}
                  className="font-semibold text-black hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LoginPromptModal;
